/**
 * Achievement Types and Definitions
 * Defines all available achievements in the system
 */

export const ACHIEVEMENT_CATEGORIES = {
  BUDGET_DISCIPLINE: 'budget_discipline',
  SAVINGS: 'savings', 
  CONSISTENCY: 'consistency',
  SEASONAL: 'seasonal',
  COLLABORATIVE: 'collaborative'
};

export const ACHIEVEMENT_TIERS = {
  BRONZE: 'bronze',
  SILVER: 'silver', 
  GOLD: 'gold',
  PLATINUM: 'platinum',
  SPECIAL: 'special'
};

export const ACHIEVEMENT_TYPES = {
  STREAK: 'streak',
  MILESTONE: 'milestone',
  PERCENTAGE: 'percentage',
  COMPARISON: 'comparison'
};

export const ACHIEVEMENT_RARITY = {
  COMMON: 'common',
  UNCOMMON: 'uncommon',
  RARE: 'rare',
  LEGENDARY: 'legendary'
};

/**
 * Core Achievement Definitions
 * These will be stored in Firestore achievements collection
 */
export const ACHIEVEMENT_DEFINITIONS = {
  // Budget Discipline Achievements
  budget_boss_1: {
    id: 'budget_boss_1',
    name: 'Budget Boss',
    description: 'Stay under budget for 1 month',
    category: ACHIEVEMENT_CATEGORIES.BUDGET_DISCIPLINE,
    type: ACHIEVEMENT_TYPES.STREAK,
    tier: ACHIEVEMENT_TIERS.BRONZE,
    rarity: ACHIEVEMENT_RARITY.COMMON,
    requirements: {
      metric: 'consecutive_months_under_budget',
      threshold: 1,
      timeframe: 'monthly'
    },
    rewards: {
      points: 100,
      badgeUrl: '/images/achievements/budget_boss_bronze.png',
      socialShareTemplate: '🎉 Just earned my Budget Boss badge! Successfully stayed under budget for 1 month with Budgley! #BudgetGoals #FinancialWellness'
    },
    isActive: true,
    isTimeRestricted: false
  },

  budget_boss_3: {
    id: 'budget_boss_3',
    name: 'Budget Boss II',
    description: 'Stay under budget for 3 consecutive months',
    category: ACHIEVEMENT_CATEGORIES.BUDGET_DISCIPLINE,
    type: ACHIEVEMENT_TYPES.STREAK,
    tier: ACHIEVEMENT_TIERS.SILVER,
    rarity: ACHIEVEMENT_RARITY.UNCOMMON,
    requirements: {
      metric: 'consecutive_months_under_budget',
      threshold: 3,
      timeframe: 'monthly'
    },
    rewards: {
      points: 300,
      badgeUrl: '/images/achievements/budget_boss_silver.png',
      socialShareTemplate: '🔥 3 months under budget streak! Earning my silver Budget Boss badge with Budgley! #BudgetChampion #FinancialDiscipline'
    },
    isActive: true,
    isTimeRestricted: false
  },

  budget_boss_6: {
    id: 'budget_boss_6',
    name: 'Budget Boss III',
    description: 'Stay under budget for 6 consecutive months',
    category: ACHIEVEMENT_CATEGORIES.BUDGET_DISCIPLINE,
    type: ACHIEVEMENT_TYPES.STREAK,
    tier: ACHIEVEMENT_TIERS.GOLD,
    rarity: ACHIEVEMENT_RARITY.RARE,
    requirements: {
      metric: 'consecutive_months_under_budget',
      threshold: 6,
      timeframe: 'monthly'
    },
    rewards: {
      points: 600,
      badgeUrl: '/images/achievements/budget_boss_gold.png',
      socialShareTemplate: '🏆 6 months under budget! Just earned my GOLD Budget Boss badge! Budgley is helping me master my finances! #BudgetMaster #MoneyGoals'
    },
    isActive: true,
    isTimeRestricted: false
  },

  master_budgeter: {
    id: 'master_budgeter',
    name: 'Master Budgeter',
    description: 'Stay under budget for 12 consecutive months',
    category: ACHIEVEMENT_CATEGORIES.BUDGET_DISCIPLINE,
    type: ACHIEVEMENT_TYPES.STREAK,
    tier: ACHIEVEMENT_TIERS.PLATINUM,
    rarity: ACHIEVEMENT_RARITY.LEGENDARY,
    requirements: {
      metric: 'consecutive_months_under_budget',
      threshold: 12,
      timeframe: 'monthly'
    },
    rewards: {
      points: 1500,
      badgeUrl: '/images/achievements/master_budgeter_platinum.png',
      socialShareTemplate: '👑 MASTER BUDGETER achieved! 12 months straight under budget! This platinum badge represents a full year of financial discipline with Budgley! #MasterBudgeter #FinancialSuccess'
    },
    isActive: true,
    isTimeRestricted: false
  },

  precision_spender_1: {
    id: 'precision_spender_1',
    name: 'Precision Spender',
    description: 'Finish within 5% of your total budget',
    category: ACHIEVEMENT_CATEGORIES.BUDGET_DISCIPLINE,
    type: ACHIEVEMENT_TYPES.PERCENTAGE,
    tier: ACHIEVEMENT_TIERS.BRONZE,
    rarity: ACHIEVEMENT_RARITY.COMMON,
    requirements: {
      metric: 'budget_variance_percentage',
      threshold: 5,
      timeframe: 'monthly',
      conditions: {
        variance_type: 'under_budget'
      }
    },
    rewards: {
      points: 150,
      badgeUrl: '/images/achievements/precision_spender_bronze.png',
      socialShareTemplate: '🎯 Precision Spender achieved! Finished within 5% of my budget - that\'s some serious planning skills! #PrecisionSpending #BudgetAccuracy'
    },
    isActive: true,
    isTimeRestricted: false
  },

  budget_beginner: {
    id: 'budget_beginner',
    name: 'Budget Beginner',
    description: 'Create your first budget',
    category: ACHIEVEMENT_CATEGORIES.BUDGET_DISCIPLINE,
    type: ACHIEVEMENT_TYPES.MILESTONE,
    tier: ACHIEVEMENT_TIERS.BRONZE,
    rarity: ACHIEVEMENT_RARITY.COMMON,
    requirements: {
      metric: 'first_budget_created',
      threshold: 1,
      timeframe: 'all_time'
    },
    rewards: {
      points: 50,
      badgeUrl: '/images/achievements/budget_beginner_bronze.png',
      socialShareTemplate: '🎯 Just created my first budget with Budgley! Taking the first step toward financial control! #BudgetBeginner #FinancialStart #MoneyGoals'
    },
    isActive: true,
    isTimeRestricted: false
  },

  budget_rookie: {
    id: 'budget_rookie',
    name: 'Budget Rookie',
    description: 'Successfully complete your first month with a budget',
    category: ACHIEVEMENT_CATEGORIES.BUDGET_DISCIPLINE,
    type: ACHIEVEMENT_TYPES.MILESTONE,
    tier: ACHIEVEMENT_TIERS.BRONZE,
    rarity: ACHIEVEMENT_RARITY.COMMON,
    requirements: {
      metric: 'first_month_completed',
      threshold: 1,
      timeframe: 'all_time'
    },
    rewards: {
      points: 200,
      badgeUrl: '/images/achievements/budget_rookie_bronze.png',
      socialShareTemplate: '🌟 Just completed my first month of budgeting with Budgley! Earning my Budget Rookie badge feels amazing! #BudgetRookie #FirstSteps #FinancialJourney'
    },
    isActive: true,
    isTimeRestricted: false
  },

  // Planning Achievements
  planning_starter: {
    id: 'planning_starter',
    name: 'Planning Starter',
    description: 'Plan to spend 5% less than your income',
    category: ACHIEVEMENT_CATEGORIES.SAVINGS,
    type: ACHIEVEMENT_TYPES.MILESTONE,
    tier: ACHIEVEMENT_TIERS.BRONZE,
    rarity: ACHIEVEMENT_RARITY.COMMON,
    requirements: {
      metric: 'cumulative_planning_surplus',
      threshold: 0.05, // 5% of total income
      timeframe: 'all_time',
      isPercentage: true
    },
    rewards: {
      points: 100,
      badgeUrl: '/images/achievements/planning_starter_bronze.png',
      socialShareTemplate: '📋 Planning Starter achieved! I\'m budgeting to spend 5% less than my income - small steps toward financial planning! #PlanningStarter #BudgetPlanning'
    },
    isActive: true,
    isTimeRestricted: false
  },

  planning_champion: {
    id: 'planning_champion',
    name: 'Planning Champion',
    description: 'Plan to spend 10% less than your income',
    category: ACHIEVEMENT_CATEGORIES.SAVINGS,
    type: ACHIEVEMENT_TYPES.MILESTONE,
    tier: ACHIEVEMENT_TIERS.SILVER,
    rarity: ACHIEVEMENT_RARITY.UNCOMMON,
    requirements: {
      metric: 'cumulative_planning_surplus',
      threshold: 0.10, // 10% of total income
      timeframe: 'all_time',
      isPercentage: true
    },
    rewards: {
      points: 250,
      badgeUrl: '/images/achievements/planning_champion_silver.png',
      socialShareTemplate: '🎊 Planning Champion earned! Budgeting for 10% surplus shows serious financial planning with Budgley! #PlanningChampion #SmartBudgeting'
    },
    isActive: true,
    isTimeRestricted: false
  },

  planning_master: {
    id: 'planning_master',
    name: 'Planning Master',
    description: 'Plan to spend 20% less than your income',
    category: ACHIEVEMENT_CATEGORIES.SAVINGS,
    type: ACHIEVEMENT_TYPES.MILESTONE,
    tier: ACHIEVEMENT_TIERS.GOLD,
    rarity: ACHIEVEMENT_RARITY.RARE,
    requirements: {
      metric: 'cumulative_planning_surplus',
      threshold: 0.20, // 20% of total income
      timeframe: 'all_time',
      isPercentage: true
    },
    rewards: {
      points: 500,
      badgeUrl: '/images/achievements/planning_master_gold.png',
      socialShareTemplate: '🏆 Planning Master achieved! Consistently planning for 20% surplus demonstrates exceptional financial discipline! #PlanningMaster #FinancialWisdom'
    },
    isActive: true,
    isTimeRestricted: false
  },

  // Consistency Achievements
  daily_tracker_7: {
    id: 'daily_tracker_7',
    name: 'Daily Tracker',
    description: 'Log expenses for 7 consecutive days',
    category: ACHIEVEMENT_CATEGORIES.CONSISTENCY,
    type: ACHIEVEMENT_TYPES.STREAK,
    tier: ACHIEVEMENT_TIERS.BRONZE,
    rarity: ACHIEVEMENT_RARITY.COMMON,
    requirements: {
      metric: 'consecutive_days_expense_logging',
      threshold: 7,
      timeframe: 'daily'
    },
    rewards: {
      points: 75,
      badgeUrl: '/images/achievements/daily_tracker_bronze.png',
      socialShareTemplate: '📝 7-day expense tracking streak! Building great financial habits with my Daily Tracker badge! #DailyTracking #FinancialHabits'
    },
    isActive: true,
    isTimeRestricted: false
  },

  daily_tracker_30: {
    id: 'daily_tracker_30',
    name: 'Daily Tracker II',
    description: 'Log expenses for 30 consecutive days',
    category: ACHIEVEMENT_CATEGORIES.CONSISTENCY,
    type: ACHIEVEMENT_TYPES.STREAK,
    tier: ACHIEVEMENT_TIERS.SILVER,
    rarity: ACHIEVEMENT_RARITY.UNCOMMON,
    requirements: {
      metric: 'consecutive_days_expense_logging',
      threshold: 30,
      timeframe: 'daily'
    },
    rewards: {
      points: 300,
      badgeUrl: '/images/achievements/daily_tracker_silver.png',
      socialShareTemplate: '🔥 30 days of expense tracking! This silver Daily Tracker badge shows consistency pays off! #MonthlyStreak #FinancialDiscipline'
    },
    isActive: true,
    isTimeRestricted: false
  },

  organization_master: {
    id: 'organization_master',
    name: 'Organization Master',
    description: 'Categorize all expenses for a full month',
    category: ACHIEVEMENT_CATEGORIES.CONSISTENCY,
    type: ACHIEVEMENT_TYPES.PERCENTAGE,
    tier: ACHIEVEMENT_TIERS.SILVER,
    rarity: ACHIEVEMENT_RARITY.UNCOMMON,
    requirements: {
      metric: 'monthly_categorization_percentage',
      threshold: 100,
      timeframe: 'monthly'
    },
    rewards: {
      points: 200,
      badgeUrl: '/images/achievements/organization_master_silver.png',
      socialShareTemplate: '📊 Organization Master achieved! 100% of my expenses are properly categorized this month! #OrganizationMaster #FinancialOrder'
    },
    isActive: true,
    isTimeRestricted: false
  }
};

/**
 * Helper function to get achievement by ID
 */
export function getAchievementById(achievementId) {
  return ACHIEVEMENT_DEFINITIONS[achievementId] || null;
}

/**
 * Helper function to get achievements by category
 */
export function getAchievementsByCategory(category) {
  return Object.values(ACHIEVEMENT_DEFINITIONS).filter(
    achievement => achievement.category === category
  );
}

/**
 * Helper function to get achievements by tier
 */
export function getAchievementsByTier(tier) {
  return Object.values(ACHIEVEMENT_DEFINITIONS).filter(
    achievement => achievement.tier === tier
  );
}

/**
 * Helper function to get all achievement IDs
 */
export function getAllAchievementIds() {
  return Object.keys(ACHIEVEMENT_DEFINITIONS);
}